import React, { useContext, useState } from 'react'
import { CurrencyContext } from '../currencyContext'
import { LandSizeUnitContext } from '../landSizeUnitContext'
import { useNavigate } from 'react-router-dom'
import { Typography, Box } from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import BedIcon from '@mui/icons-material/Bed'
import BathroomIcon from '@mui/icons-material/Bathroom'
import ArchitectureIcon from '@mui/icons-material/Architecture'
import Divider from './Divider'
import * as ROUTES from '../routes'
import OverLayComponent from './overlayComponent'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'

   const buildImageUrl = (path) => {
    if (!path) return null
    if (path.startsWith('http')) return path.replace('https//', 'https://')
    return `${BASE_URL}${path}`
}

/**
 * Displays a thumbnail card for a property.
 * Expects the PropertyListSerializer shape.
 * Height is fully consistent across all cards via flex column + flex-grow on the content area.
 */
function PropertyThumbnail(props) {
    const currency    = useContext(CurrencyContext).value
    const landSizeUnit = useContext(LandSizeUnitContext).value
    const item = props.item

    const slug           = item?.slug
    const title          = item?.title || ''
    const isSold         = item?.status === 'sold'
    const isUnderOffer   = item?.status === 'under_offer'
    const isFeatured     = item?.featured === true
    const categoryDisplay = item?.category_display || ''

    const mainImage = item.main_image ? buildImageUrl(item.main_image) : null

    const rawPrice = parseFloat(item?.display_price || 0)
    const price    = rawPrice * currency.value

    const areaSqft     = item?.area_sqft ? parseFloat(item.area_sqft) : null
    const sizeDisplay  = areaSqft ? (areaSqft * landSizeUnit.value).toFixed(0) : null

    const rooms = item?.room_count || {}
    const beds  = rooms.bedrooms ?? null
    const baths = rooms.bathrooms ?? null

    const location = item?.location || ''
    const parish   = item?.parish_display || ''
    const address  = [location, parish].filter(Boolean).join(', ')

    const [hover,     toggleHover]   = useState(false)
    const [imgLoaded, setImgLoaded]  = useState(false)
    const navigate = useNavigate()

 

    function onClick() {
        navigate(`/${ROUTES.PROPERTIES}/${slug}`)
    }

    return (
        /* Outer wrapper: full height column so all cards in a Grid row stretch equally */
        <Box
            onMouseEnter={() => toggleHover(true)}
            onMouseLeave={() => toggleHover(false)}
            onClick={onClick}
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',       /* fill the Grid column */
                borderRadius: '10px',
                overflow: 'hidden',
                cursor: 'pointer',
                bgcolor: 'background.paper',
                boxShadow: hover ? 4 : 1,
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                transform: hover ? 'translateY(-3px)' : 'none',
            }}
        >
            <OverLayComponent style={{ borderRadius: '10px', pointerEvents: 'none' }} hover={hover} />

            {/* ── Image block ── */}
            <Box sx={{ position: 'relative', height: 200, flexShrink: 0, bgcolor: '#E8ECF0' }}>
                {mainImage ? (
                    <img
                        src={mainImage}
                        alt={title}
                        onLoad={() => setImgLoaded(true)}
                        style={{
                            display: 'block',
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover',
                            /* Point 3 — fade in once loaded */
                            opacity: imgLoaded ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                        }}
                        loading='lazy'
                    />
                ) : (
                    <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant='caption' color='text.dimGrey'>No image</Typography>
                    </Box>
                )}

                {/* Point 1 — Featured badge */}
                {isFeatured && (
                    <Box sx={{
                        position: 'absolute', top: 10, left: 10,
                        bgcolor: '#B8860B',
                        color: '#fff',
                        display: 'flex', alignItems: 'center', gap: 0.4,
                        px: 1, py: 0.25,
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        boxShadow: 1,
                    }}>
                        <StarIcon sx={{ fontSize: 12 }} />
                        Featured
                    </Box>
                )}

                {/* Status banners */}
                {isSold && (
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', bgcolor: '#b71c1ccc', py: '3px', textAlign: 'center' }}>
                        <Typography variant='subtitle2' sx={{ color: '#fff', m: 0, fontSize: '0.75rem', fontWeight: 700 }}>SOLD</Typography>
                    </Box>
                )}
                {!isSold && isUnderOffer && (
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', bgcolor: '#e65100cc', py: '3px', textAlign: 'center' }}>
                        <Typography variant='subtitle2' sx={{ color: '#fff', m: 0, fontSize: '0.75rem', fontWeight: 700 }}>UNDER OFFER</Typography>
                    </Box>
                )}
            </Box>

            {/* ── Content block — flex-grow fills remaining height so all cards align ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, px: 1.25, pt: 1.25, pb: 1.5 }}>
                {/* Category */}
                <Typography variant='overline' color='text.dimGrey' sx={{ display: 'block', lineHeight: 1.4, mb: 0.25 }}>
                    {categoryDisplay}
                </Typography>

                {/* Title — clamped to 2 lines so all cards stay the same height */}
                <Typography variant='subtitle1' sx={{
                    fontWeight: 600,
                    lineHeight: 1.4,
                    mb: 0.5,
                    color: 'text.primary',
                    height: '2.8em',          /* exactly 2 lines at lineHeight 1.4 */
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                }}>
                    {title || '\u00A0'}
                </Typography>

                {/* Price */}
                <Typography variant='h6' sx={{ color: 'text.primaryGreen', fontWeight: 700, mb: 0.5 }}>
                    {price.toLocaleString('en-US', { style: 'currency', currency: currency.name })}
                </Typography>

                <Divider />

                {/* Specs row */}
                <Box sx={{ display: 'flex', gap: 1, py: 0.75, flexWrap: 'wrap' }}>
                    {beds != null && (
                        <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <BedIcon sx={{ fontSize: 14 }} /> {beds} Bed
                        </Typography>
                    )}
                    {baths != null && (
                        <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <BathroomIcon sx={{ fontSize: 14 }} /> {baths} Bath
                        </Typography>
                    )}
                    {sizeDisplay && (
                        <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <ArchitectureIcon sx={{ fontSize: 14 }} /> {sizeDisplay} {landSizeUnit.name}
                        </Typography>
                    )}
                </Box>

                <Divider />

                {/* Address — clamped to 1 line */}
                <Typography variant='caption' color='text.secondary' sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 0.75,
                    height: '1.5em',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                }}>
                    {address || '\u00A0'}
                </Typography>
            </Box>

            {/* Admin action buttons injected by parent */}
            {props.children && <Box sx={{ px: 1, pb: 1 }}>{props.children}</Box>}
        </Box>
    )
}

export default React.memo(PropertyThumbnail)
